import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Mail, Trash2, Copy, Check, AlertTriangle, Sparkles, Minus, Plus } from 'lucide-react';
import { useAuthStore, useFeatureFlagStore } from '@shared/state';
import { RECALIBRATION_FLAGS } from '@shared/constants';
import {
  createTeam,
  inviteToTeam,
  listTeamForAdmin,
  revokeTeamInvite,
  removeTeamMember,
  purchaseCoachSeats,
  assignCoachSeat,
  revokeCoachSeat,
  type TeamSummary,
  type TeamMemberRow,
  type TeamPendingInvite,
} from '@shared/services/teams';
import { COACH_ADDON } from '@shared/constants/tiers';
import type { RecalibrationPreference, UserProfile } from '@shared/types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { TeamLeaderboard } from '../components/gamification/TeamLeaderboard';
import { RecalibrationSettings } from '../components/recalibration/RecalibrationSettings';
import { FullWizard } from '../components/recalibration/FullWizard';

interface UserDocLike {
  teamAdminOf?: string | null;
}

export function TeamPage() {
  const user = useAuthStore((s) => s.user);
  const [teamId, setTeamId] = useState<string | null>(
    (user as (UserDocLike & typeof user) | null)?.teamAdminOf ?? null,
  );
  const [team, setTeam] = useState<TeamSummary | null>(null);
  const [members, setMembers] = useState<TeamMemberRow[]>([]);
  const [invites, setInvites] = useState<TeamPendingInvite[]>([]);
  const [loading, setLoading] = useState(!!teamId);
  const [error, setError] = useState<string | null>(null);
  const [fullWizardOpen, setFullWizardOpen] = useState(false);
  const coachEnabled = useFeatureFlagStore((s) => s.isEnabled(RECALIBRATION_FLAGS.COACH));

  async function refresh(id: string) {
    try {
      const data = await listTeamForAdmin(id);
      setTeam(data.team);
      setMembers(data.members);
      setInvites(data.pendingInvites);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    if (teamId) refresh(teamId);
    // Try to discover an existing team when user doc didn't surface teamAdminOf
    if (!teamId) setLoading(false);
  }, [user, teamId]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold mb-1">Team</h1>
        <p className="text-sm text-content-muted font-body mb-6">
          {team ? team.name : 'Manage your CogniPrint team'}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm font-body">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && !teamId && (
          <CreateTeamCard onCreated={(id) => { setTeamId(id); setLoading(true); refresh(id); }} />
        )}

        {!loading && teamId && team && (
          <>
            <TeamOverview team={team} members={members} invites={invites} />
            <div className="mb-4">
              <TeamLeaderboard teamId={teamId} viewerUid={user.uid} />
            </div>
            <InviteCard
              teamId={teamId}
              seatsAvailable={Math.max(0, team.seatsPurchased - team.seatsUsed - team.seatsPending)}
              onInvited={() => refresh(teamId)}
            />
            {coachEnabled && (
              <CoachSeatsCard
                teamId={teamId}
                team={team}
                members={members}
                onChange={() => refresh(teamId)}
              />
            )}
            <MembersList
              teamId={teamId}
              members={members}
              coachSeatPool={coachEnabled ? team.coachSeatPool : null}
              onChange={() => refresh(teamId)}
            />
            <InvitesList
              invites={invites}
              onChange={() => refresh(teamId)}
            />

            {/* Your own recalibration — mirrors the Account & Billing page */}
            <RecalibrationSettings
              currentPreference={
                (user as UserProfile).recalibrationPreference ??
                ('default' as RecalibrationPreference)
              }
              onStartWizard={() => setFullWizardOpen(true)}
            />
          </>
        )}
      </motion.div>

      <FullWizard open={fullWizardOpen} onClose={() => setFullWizardOpen(false)} />
    </div>
  );
}

function CreateTeamCard({ onCreated }: { onCreated: (teamId: string) => void }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const result = await createTeam(name.trim());
      onCreated(result.teamId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card padding="lg">
      <div className="flex items-start gap-3 mb-3">
        <Users size={18} className="text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-lg font-semibold mb-1">Create a team</h2>
          <p className="text-xs text-content-muted font-body">
            Teams let you invite members and see aggregate progress. You can create one team per account.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="flex items-end gap-3">
        <label className="flex-1">
          <span className="block text-xs font-body text-content-muted mb-1">Team name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
            placeholder="e.g. Acme Compliance Cohort"
            className="w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors"
          />
        </label>
        <Button type="submit" loading={submitting} disabled={!name.trim()}>
          Create
        </Button>
      </form>
      {err && <p className="mt-3 text-sm text-error font-body">{err}</p>}
    </Card>
  );
}

function TeamOverview({
  team, members, invites,
}: {
  team: TeamSummary;
  members: TeamMemberRow[];
  invites: TeamPendingInvite[];
}) {
  const totalUploads = members.reduce((sum, m) => sum + m.uploadCount, 0);
  const totalPaths = members.reduce((sum, m) => sum + m.pathCount, 0);

  return (
    <Card className="mb-4">
      <div className="grid sm:grid-cols-4 gap-4">
        <OverviewStat label="Members" value={members.length} />
        <OverviewStat label="Seats (incl. founder)" value={`${team.seatsUsed} / ${team.seatsPurchased}`} />
        <OverviewStat label="Uploads" value={totalUploads} />
        <OverviewStat label="Learning paths" value={totalPaths} />
      </div>
      {invites.length > 0 && (
        <p className="mt-4 text-xs text-content-muted font-body">
          {invites.length} pending invite{invites.length === 1 ? '' : 's'} · Team ID: <span className="font-mono">{team.teamId.slice(0, 8)}</span>
        </p>
      )}
    </Card>
  );
}

function OverviewStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-[10px] text-content-dim uppercase tracking-wider font-body">{label}</div>
      <div className="font-heading text-2xl font-bold text-content">{value}</div>
    </div>
  );
}

function InviteCard({
  teamId, seatsAvailable, onInvited,
}: {
  teamId: string;
  seatsAvailable: number;
  onInvited: () => void;
}) {
  const [raw, setRaw] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const full = seatsAvailable <= 0;

  async function send() {
    setErr(null);
    setResult(null);
    const emails = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    setSending(true);
    try {
      const data = await inviteToTeam(teamId, emails);
      setResult({ created: data.invites.length, skipped: data.skipped });
      setRaw('');
      onInvited();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to send invites');
    } finally {
      setSending(false);
    }
  }

  return (
    <Card className="mb-4">
      <div className="flex items-start gap-3 mb-3">
        <Mail size={18} className="text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-lg font-semibold mb-1">Invite members</h2>
          <p className="text-xs text-content-muted font-body">
            Paste a comma- or newline-separated email list. Each person joins the team on signup (or from their next login).
          </p>
          <p className={`text-xs font-body mt-1 ${full ? 'text-gold-400' : 'text-content-muted'}`}>
            {full
              ? 'No seats available — remove a member or add licenses to invite more.'
              : `${seatsAvailable} seat${seatsAvailable === 1 ? '' : 's'} available to invite.`}
          </p>
        </div>
      </div>
      <textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={4}
        disabled={full}
        placeholder="alice@example.com, bob@example.com&#10;charlie@example.com"
        className="w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors resize-y disabled:opacity-50"
      />
      <div className="flex items-center justify-end gap-3 mt-3">
        {result && (
          <span className="text-xs text-success font-body">
            {result.created} invited
            {result.skipped.length > 0 && ` · ${result.skipped.length} skipped`}
          </span>
        )}
        <Button onClick={send} loading={sending} disabled={!raw.trim() || full}>
          Send invites
        </Button>
      </div>
      {err && <p className="mt-3 text-sm text-error font-body">{err}</p>}
      {result && result.skipped.length > 0 && (
        <details className="mt-3 text-xs text-content-muted font-body">
          <summary className="cursor-pointer">Skipped ({result.skipped.length})</summary>
          <ul className="mt-2 space-y-0.5">
            {result.skipped.map((s, i) => <li key={i} className="font-mono">{s}</li>)}
          </ul>
        </details>
      )}
    </Card>
  );
}

function CoachSeatsCard({
  teamId, team, members, onChange,
}: {
  teamId: string;
  team: TeamSummary;
  members: TeamMemberRow[];
  onChange: () => void;
}) {
  const pool = team.coachSeatPool;
  const assigned = pool?.assigned ?? members.filter((m) => m.coachAssigned).length;
  const purchased = pool?.purchased ?? 0;
  const available = Math.max(0, purchased - assigned);

  const [target, setTarget] = useState(purchased);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Keep the stepper in sync if the pool changes underneath us.
  useEffect(() => { setTarget(purchased); }, [purchased]);

  const dirty = target !== purchased;
  const belowAssigned = target < assigned;

  async function save() {
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const result = await purchaseCoachSeats(teamId, target);
      // A paid increase returns a Lemon Squeezy checkout URL — send the admin
      // there; the pool reconciles via webhook on payment confirmation.
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to update Coach seats');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mb-4">
      <div className="flex items-start gap-3 mb-4">
        <Sparkles size={18} className="text-gold-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-lg font-semibold mb-1">Coach seats</h2>
          <p className="text-xs text-content-muted font-body">
            Coach is an add-on (${COACH_ADDON.monthlyPrice.toFixed(2)}/seat per month) you assign to
            individual members — it unlocks bi-weekly recalibration, the counterfactual simulator,
            and auto-pilot. Buy a pool of seats, then assign them in the member list below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <SeatStat label="Purchased" value={purchased} />
        <SeatStat label="Assigned" value={assigned} />
        <SeatStat label="Available to assign" value={available} accent />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <span className="block text-xs font-body text-content-muted mb-1">Seats in pool</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTarget((t) => Math.max(assigned, t - 1))}
              disabled={saving || target <= assigned}
              aria-label="Remove a seat"
              className="p-2 rounded-lg bg-dark-subtle border border-dark-border text-content-muted hover:text-content disabled:opacity-40 transition-colors cursor-pointer"
            >
              <Minus size={14} />
            </button>
            <span className="w-10 text-center font-heading text-lg font-bold tabular-nums">{target}</span>
            <button
              type="button"
              onClick={() => setTarget((t) => t + 1)}
              disabled={saving}
              aria-label="Add a seat"
              className="p-2 rounded-lg bg-dark-subtle border border-dark-border text-content-muted hover:text-content disabled:opacity-40 transition-colors cursor-pointer"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
        <Button onClick={save} loading={saving} disabled={!dirty || belowAssigned}>
          {target > purchased ? 'Buy seats' : 'Update seats'}
        </Button>
        {saved && <span className="text-xs text-success font-body mb-2.5">Seats updated</span>}
      </div>

      {belowAssigned && (
        <p className="mt-3 text-xs text-gold-400 font-body">
          Revoke {assigned - target} assigned seat{assigned - target === 1 ? '' : 's'} before reducing
          the pool to {target}.
        </p>
      )}
      {err && <p className="mt-3 text-sm text-error font-body">{err}</p>}
      <p className="mt-3 text-[11px] text-content-dim font-body">
        Billing reconciles through your Lemon Squeezy subscription when the seat quantity changes.
      </p>
    </Card>
  );
}

function SeatStat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-dark-border bg-dark-bg/40 px-3 py-2.5">
      <div className="text-[10px] text-content-dim uppercase tracking-wider font-body">{label}</div>
      <div className={`font-heading text-2xl font-bold ${accent ? 'text-gold-400' : 'text-content'}`}>
        {value}
      </div>
    </div>
  );
}

function MembersList({
  teamId, members, coachSeatPool, onChange,
}: {
  teamId: string;
  members: TeamMemberRow[];
  coachSeatPool: TeamSummary['coachSeatPool'];
  onChange: () => void;
}) {
  const [removingUid, setRemovingUid] = useState<string | null>(null);
  const [coachBusyUid, setCoachBusyUid] = useState<string | null>(null);

  const hasPool = (coachSeatPool?.purchased ?? 0) > 0;
  const available = Math.max(0, (coachSeatPool?.purchased ?? 0) - (coachSeatPool?.assigned ?? 0));

  async function remove(uid: string) {
    if (!confirm('Remove this member from the team?')) return;
    setRemovingUid(uid);
    try {
      await removeTeamMember(teamId, uid);
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to remove member');
    } finally {
      setRemovingUid(null);
    }
  }

  async function toggleCoach(m: TeamMemberRow) {
    setCoachBusyUid(m.uid);
    try {
      if (m.coachAssigned) {
        await revokeCoachSeat(teamId, m.uid);
      } else {
        await assignCoachSeat(teamId, m.uid);
      }
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to update Coach seat');
    } finally {
      setCoachBusyUid(null);
    }
  }

  return (
    <Card className="mb-4">
      <h2 className="font-heading text-lg font-semibold mb-4">Members ({members.length})</h2>
      <div className="rounded-xl border border-dark-border overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-body uppercase tracking-wider text-content-dim bg-dark-bg/40 border-b border-dark-border">
          <div className="col-span-4">Member</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-1 text-right">Uploads</div>
          <div className="col-span-1 text-right">Paths</div>
          <div className="col-span-3 text-right">{hasPool ? 'Coach' : ''}</div>
          <div className="col-span-1"></div>
        </div>
        {members.map((m) => {
          const canAssign = m.coachAssigned || available > 0;
          return (
            <div
              key={m.uid}
              className="sm:grid sm:grid-cols-12 sm:gap-2 px-4 py-3 text-sm font-body border-t border-dark-border first:border-t-0 flex items-center justify-between flex-wrap"
            >
              <div className="sm:col-span-4 flex items-center gap-2 min-w-0">
                <div className="min-w-0">
                  <div className="font-semibold text-content truncate">
                    {m.displayName ?? m.email ?? m.uid.slice(0, 8)}
                    {m.isAdmin && <span className="ml-2 px-1.5 py-0.5 rounded-pill text-[9px] font-bold uppercase tracking-wider bg-gold-500/15 text-gold-400">Admin</span>}
                  </div>
                  {m.displayName && m.email && (
                    <div className="text-xs text-content-muted truncate">{m.email}</div>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2 text-content-secondary capitalize">{m.subscription}</div>
              <div className="sm:col-span-1 text-content-secondary sm:text-right">{m.uploadCount}</div>
              <div className="sm:col-span-1 text-content-secondary sm:text-right">{m.pathCount}</div>
              <div className="sm:col-span-3 sm:text-right mt-2 sm:mt-0">
                {hasPool && (
                  <button
                    onClick={() => toggleCoach(m)}
                    disabled={coachBusyUid === m.uid || (!m.coachAssigned && !canAssign)}
                    title={
                      !m.coachAssigned && !canAssign
                        ? 'No Coach seats available — buy more above'
                        : m.coachAssigned ? 'Revoke Coach seat' : 'Assign Coach seat'
                    }
                    className={
                      m.coachAssigned
                        ? 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11px] font-semibold bg-gold-500/15 text-gold-400 border border-gold-500/30 hover:bg-gold-500/25 transition-colors cursor-pointer disabled:opacity-50'
                        : 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11px] font-semibold bg-dark-subtle text-content-muted border border-dark-border hover:text-content transition-colors cursor-pointer disabled:opacity-40'
                    }
                  >
                    {m.coachAssigned ? <><Sparkles size={11} /> Coach</> : 'Assign Coach'}
                  </button>
                )}
              </div>
              <div className="sm:col-span-1 text-right">
                {!m.isAdmin && (
                  <button
                    onClick={() => remove(m.uid)}
                    disabled={removingUid === m.uid}
                    aria-label="Remove member"
                    className="p-1 text-content-dim hover:text-error transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function InvitesList({ invites, onChange }: { invites: TeamPendingInvite[]; onChange: () => void }) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function copyInviteLink(code: string) {
    const url = `${window.location.origin}/signup?invite=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      prompt('Copy invite link:', url);
    }
  }

  async function revoke(code: string) {
    if (!confirm('Revoke this invite?')) return;
    setRevoking(code);
    try {
      await revokeTeamInvite(code);
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to revoke');
    } finally {
      setRevoking(null);
    }
  }

  if (invites.length === 0) return null;

  return (
    <Card className="mb-4">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle size={16} className="text-gold-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-lg font-semibold mb-1">Pending invites ({invites.length})</h2>
          <p className="text-xs text-content-muted font-body">
            These people will join automatically when they sign up. Copy the invite link to share via your own channels until in-app email delivery lands.
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-dark-border overflow-hidden">
        {invites.map((inv) => (
          <div key={inv.code} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-dark-border first:border-t-0">
            <div className="min-w-0">
              <div className="text-sm font-body font-semibold text-content truncate">{inv.email}</div>
              <div className="text-[11px] text-content-muted font-body">
                Invited {new Date(inv.invitedAt).toLocaleDateString()} · expires {new Date(inv.expiresAt).toLocaleDateString()}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => copyInviteLink(inv.code)}
                className="p-1.5 text-content-muted hover:text-content transition-colors cursor-pointer"
                aria-label="Copy invite link"
                title="Copy invite link"
              >
                {copiedCode === inv.code ? <Check size={14} className="text-success" /> : <Copy size={14} />}
              </button>
              <button
                onClick={() => revoke(inv.code)}
                disabled={revoking === inv.code}
                className="p-1.5 text-content-dim hover:text-error transition-colors cursor-pointer disabled:opacity-50"
                aria-label="Revoke invite"
                title="Revoke"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
